import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { Box, Divider, Drawer, FormControlLabel, IconButton, Switch, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { OmnichannelCommand } from '../../../services/command';
import { SPU, StorePickUpRow } from '../storePickUp.classes';

interface StorePickUpEditDrawerProps {
    isOpen: boolean;
    editRow: StorePickUpRow | null;
    onClose: () => void;
    onRefreshList: () => Promise<void>;
}

export const StorePickUpEditDrawer: FC<StorePickUpEditDrawerProps> = ({ isOpen, editRow, onClose, onRefreshList }) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [capacity, setCapacity] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (editRow) {
            setIsVisible(editRow.visible);
            setCapacity(editRow.capacity != null ? String(editRow.capacity) : '');
        }
    }, [editRow]);

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    const parseCapacity = (): number | null => {
        const trimmed = capacity.trim();
        if (trimmed === '') {
            return null;
        }
        const value = Number(trimmed);
        if (!Number.isFinite(value) || value < 0 || !Number.isInteger(value)) {
            return null;
        }
        return value;
    };

    const handleSave = () => {
        if (!editRow) {
            return;
        }

        const nextCapacity = parseCapacity();
        const capacityChanged = nextCapacity !== editRow.capacity;
        const visibilityChanged = isVisible !== editRow.visible;

        if (!capacityChanged && !visibilityChanged) {
            Swal.fire(t('storePickUp.noChanges'), '', 'info');
            return;
        }

        if (capacityChanged && nextCapacity === null) {
            Swal.fire(t('storePickUp.invalidCapacity'), '', 'warning');
            return;
        }

        Swal.fire({
            text: t('general.updateConfirmationText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.update'),
        }).then(async (result) => {
            if (!result.isConfirmed || !editRow) {
                return;
            }

            setIsLoading(true);
            try {
                if (visibilityChanged) {
                    await OmnichannelCommand.PostUpdateStorePickup({
                        storeId: editRow.id,
                        isVisible,
                    });
                }

                if (capacityChanged && nextCapacity !== null) {
                    await OmnichannelCommand.PostUpsertStoreCapacity({
                        storeCode: editRow.storeCode,
                        capacity: nextCapacity,
                    });
                }

                onClose();
                await onRefreshList();
                Swal.fire(t('general.updated'), ' ', 'success');
            } catch (error) {
                console.log('Store pickup update error :>> ', error);
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            } finally {
                setIsLoading(false);
            }
        });
    };

    return (
        <Drawer anchor="right" open={isOpen} onClose={handleClose} className={SPU.editDrawer}>
            <Box sx={{ width: 400, maxWidth: '100vw' }} className={SPU.editDrawerBody}>
                {isLoading && <SCUILoadingLinear />}
                <Box
                    className={SPU.editDrawerHeader}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon color="primary" />
                        <Typography variant="h6">{t('storePickUp.editDrawerTitle')}</Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        className={SPU.btnClose}
                        aria-label={t('general.close')}
                        disabled={isLoading}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('general.storeCode')}: {editRow?.storeCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('general.storeName')}: {editRow?.name}
                    </Typography>
                    <FormControlLabel
                        className={SPU.inputVisibility}
                        control={
                            <Switch
                                checked={isVisible}
                                onChange={(e) => setIsVisible(e.target.checked)}
                                disabled={isLoading}
                            />
                        }
                        label={t('storePickUp.statusLabel')}
                    />
                    <TextField
                        className={SPU.inputCapacity}
                        label={t('storePickUp.capacity')}
                        type="number"
                        fullWidth
                        value={capacity}
                        onChange={(e) => setCapacity(e.target.value)}
                        disabled={isLoading}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {t('storePickUp.remainingCapacity')}:{' '}
                        {editRow?.remainingCapacity != null ? editRow.remainingCapacity : '-'}
                    </Typography>
                </Box>
                <Divider />
                <Box
                    className={SPU.editDrawerFooter}
                    sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}
                >
                    <SCUIButton
                        text={t('general.cancel')}
                        type="secondary"
                        sizeType="fixed"
                        size="s"
                        onClick={handleClose}
                        disabled={isLoading}
                    />
                    <SCUIButton
                        className={SPU.btnSave}
                        text={t('general.update')}
                        type="primary"
                        sizeType="fixed"
                        size="s"
                        onClick={handleSave}
                        disabled={isLoading}
                    />
                </Box>
            </Box>
        </Drawer>
    );
};
